import { colorTokens, KariusButton, KariusLink, KariusTextField, KariusAlert } from '@kariusdx/design-system';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { CircularProgress, Grid, IconButton, Typography, useTheme } from '@mui/material';
import Link from '@mui/material/Link';
import { useFormik } from 'formik';
import React from 'react';
import * as yup from 'yup';
import { EngagementEvents, logKariusEvent } from '../../Analytics';
import { LoginCredentials, LoginFormProps } from '../../auth/authTypes';
import { privacyPolicy, termsOfUse } from '../../Config/links';
import getTestId from '../Components/GetTestId';

export default function LoginForm({ onSubmit, loading = false, onResetPasswordClick, showUnblockSuccessAlert = false }: LoginFormProps) {
  const theme = useTheme();
  const [showPassword, setShowPassword] = React.useState(false);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const validationSchema = yup.object({
    email: yup.string().trim().required('An email or username is required for sign in'),
    password: yup.string().trim().required('A password is required for sign in'),
  });

  const { errors, touched, values, handleChange, handleSubmit, setErrors } = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: validationSchema,
    onSubmit: (values: LoginCredentials) => {
      onSubmit(values, showLoginError);
    },
  });

  const showLoginError = (message: any) => {
    if (message) {
      setErrors({ password: message, email: ' ' });
      return;
    }
  };

  return (
    <Grid
        container
        component='form'
        sx={{
          maxWidth: '398px',
        }}
        onSubmit={handleSubmit}
      >
        {showUnblockSuccessAlert && (
          <Grid item xs={12} mb={theme.spacing(5)}>
            <KariusAlert variant='success'>Your account has been unlocked. Please sign in.</KariusAlert>
          </Grid>
        )}
        <Grid item xs={12}>
          <KariusTextField
            fullWidth
            id='email'
            name='email'
            label='Email or username'
            value={values.email}
            onChange={handleChange}
            error={touched.email && !!errors.email}
            helperText={(touched.email && errors.email) || ' '}
            InputLabelProps={{ shrink: undefined }}
            inputProps={{
              'data-test': getTestId('email'),
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <KariusTextField
            fullWidth
            id='password'
            name='password'
            label='Password'
            value={values.password}
            onChange={handleChange}
            error={touched.password && !!errors.password}
            helperText={(touched.password && errors.password) || ' '}
            type={showPassword ? undefined : 'password'}
            InputLabelProps={{ shrink: undefined }}
            inputProps={{
              'data-test': getTestId('password'),
            }}
            icon={
              <IconButton
                aria-label='toggle password visibility'
                onClick={handleClickShowPassword}
                onMouseDown={handleMouseDownPassword}
                size='medium'
                sx={{ marginRight: '-8px' }}
              >
                {showPassword ? (
                  <VisibilityIcon sx={{ width: 24, height: 24, fill: colorTokens.icon_03 }} />
                ) : (
                  <VisibilityOffIcon sx={{ width: 24, height: 24, fill: colorTokens.icon_03 }} />
                )}
              </IconButton>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <KariusButton
            variant='primary'
            rounded
            type='submit'
            sx={{
              width: '100%',
              marginTop: theme.spacing(5),
              marginBottom: theme.spacing(5),
            }}
            data-test={getTestId('sign-in')}
          >
            {loading ? <CircularProgress size={25} sx={{ color: colorTokens.ui_01 }} /> : 'Sign In'}
          </KariusButton>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'center', marginBottom: theme.spacing(5) }}>
          <Typography variant='body_02'>
            <Link
              sx={{
                fontSize: '14px',
                lineHeight: '20px',
                letterSpacing: '0.25px',
                fontWeight: '400',
                color: colorTokens.text_link,
                cursor: 'pointer',
              }}
              onClick={(event) => {
                onResetPasswordClick();
                logKariusEvent(EngagementEvents.reset_password_tapped);
                event.preventDefault();
              }}
            >
              Reset password
            </Link>
          </Typography>
        </Grid>
        <Grid item xs={12} sx={{ textAlign: 'center' }}>
          <Typography align='center' variant='body_01'>
            By signing in, you agree to the
            <br />{' '}
            <KariusLink
              href={termsOfUse}
              underline
              target='_blank'
              rel='noreferrer'
              onClick={() => logKariusEvent(EngagementEvents.tou_tapped)}
            >
              Terms of Use
            </KariusLink>{' '}
            and{' '}
            <KariusLink
              href={privacyPolicy}
              underline
              target='_blank'
              rel='noreferrer'
              onClick={() => logKariusEvent(EngagementEvents.privacy_tapped)}
            >
              Privacy Policy
            </KariusLink>
          </Typography>
        </Grid>
      </Grid>
  );
}
